import { Check, X } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { QuizQuestion } from "../content/types";
import { useContentText } from "../content/useContentText";
import { feedbackBox, feedbackTitle } from "./feedbackStyles";
import { GameFrame } from "./GameFrame";

/**
 * One question at a time: pick an option, see right / wrong and why, then « Suivant ». Every option is a real
 * button (keyboard and screen-reader operable); after the answer the focus moves to « Suivant ».
 */
export const QuizGame = ({
  questions,
  onFinish,
  onQuit,
  explanationLink,
}: {
  questions: readonly QuizQuestion[];
  /** A link shown after the explanation (the product course points to « Tout sur Gavion »). */
  explanationLink?: { to: string; label: string };
  onFinish: (correct: number, total: number) => void;
  onQuit: () => void;
}) => {
  const t = useTranslate();
  const text = useContentText();
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [correct, setCorrect] = useState(0);
  const nextRef = useRef<HTMLButtonElement>(null);
  const promptRef = useRef<HTMLHeadingElement>(null);
  const q = questions[index];
  const answered = picked !== null;
  const right = picked === q.answer;
  const last = index === questions.length - 1;

  useEffect(() => {
    if (answered) nextRef.current?.focus();
  }, [answered]);
  useEffect(() => {
    if (index > 0) promptRef.current?.focus();
  }, [index]);

  const pick = (id: string) => {
    if (answered) return;
    setPicked(id);
    if (id === q.answer) setCorrect((c) => c + 1);
  };
  const next = () => {
    if (last) {
      onFinish(correct, questions.length);
      return;
    }
    setIndex((i) => i + 1);
    setPicked(null);
  };
  const answerOption = q.options.find((o) => o.id === q.answer)!;
  // some product questions have no explanation wording: show nothing rather than an empty paragraph
  const explanation = text(q.explanationKey, q.explanationArgs);

  return (
    <GameFrame
      step={t("gavion.training.game.question", {
        n: index + 1,
        total: questions.length,
      })}
      current={index + (answered ? 1 : 0)}
      total={questions.length}
      onQuit={onQuit}
    >
      <h2
        ref={promptRef}
        tabIndex={-1}
        id="gavion-quiz-prompt"
        className="text-lg leading-snug font-bold outline-none"
      >
        {text(q.promptKey, q.promptArgs)}
      </h2>
      <div
        role="group"
        aria-labelledby="gavion-quiz-prompt"
        className={cn(
          "grid gap-2",
          q.options.length > 3 ? "sm:grid-cols-2" : "",
        )}
      >
        {q.options.map((o) => {
          const isAnswer = answered && o.id === q.answer;
          const isWrongPick = answered && o.id === picked && !right;
          return (
            <button
              key={o.id}
              type="button"
              disabled={answered}
              aria-pressed={picked === o.id}
              onClick={() => pick(o.id)}
              className={cn(
                "flex min-h-11 w-full items-center gap-3 rounded-lg border bg-background px-3 py-2 text-left text-sm font-medium transition-colors",
                "hover:border-primary hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                "disabled:cursor-default",
                isAnswer &&
                  "border-green-600 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100",
                isWrongPick &&
                  "border-destructive bg-destructive/10 text-foreground",
                answered && !isAnswer && !isWrongPick && "opacity-60",
              )}
            >
              {o.hotkey && (
                <kbd className="inline-flex size-6 shrink-0 items-center justify-center rounded border bg-muted font-mono text-xs">
                  {o.hotkey}
                </kbd>
              )}
              <span className="flex-1">{text(o.labelKey, o.labelArgs)}</span>
              {isAnswer && (
                <>
                  <Check className="size-4 shrink-0" aria-hidden="true" />
                  <span className="sr-only">
                    {t("gavion.training.game.right_option")}
                  </span>
                </>
              )}
              {isWrongPick && (
                <>
                  <X className="size-4 shrink-0" aria-hidden="true" />
                  <span className="sr-only">
                    {t("gavion.training.game.your_option")}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
      <div role="status" aria-live="polite">
        {answered && (
          <div className={cn("space-y-1", feedbackBox(right))}>
            <p className={feedbackTitle(right)}>
              {right ? (
                <Check className="size-4 shrink-0" aria-hidden="true" />
              ) : (
                <X className="size-4 shrink-0" aria-hidden="true" />
              )}
              {right
                ? t("gavion.training.game.correct")
                : t("gavion.training.game.wrong")}
            </p>
            {!right && (
              <p>
                {t("gavion.training.game.answer_was", {
                  answer: text(answerOption.labelKey, answerOption.labelArgs),
                })}
              </p>
            )}
            {explanation && <p>{explanation}</p>}
            {explanationLink && (
              <p>
                <Link
                  to={explanationLink.to}
                  className="text-sm font-semibold text-primary underline"
                >
                  {explanationLink.label}
                </Link>
              </p>
            )}
          </div>
        )}
      </div>
      {answered && (
        <Button ref={nextRef} onClick={next} className="self-end">
          {last
            ? t("gavion.training.game.finish")
            : t("gavion.training.game.next")}
        </Button>
      )}
    </GameFrame>
  );
};
